/**
 * Result-Panel-Tab im KI-Assistent-Drawer.
 *
 * - Header: Dropdown der letzten Panels einer Konversation, neuestes oben
 * - Body oben: kurze LLM-Antwort, die zu diesem Panel gehört
 * - Body unten: Items als Tabelle (Desktop) oder Cards (Mobile), mit Links
 *
 * Daten kommen über GET /assistant/panels/:id und
 * GET /assistant/conversations/:id/panels (siehe Plan RP-5).
 */
import { authFetch, useAuth } from '@onvice/portal-shell-api';

import { useEffect, useMemo, useState } from 'react';
import { Loader2, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const ASSISTANT_BASE =
  (import.meta.env.VITE_ASSISTANT_URL as string | undefined)?.replace(/\/+$/, '') || '/api/modules/assistant';

interface Badge {
  label: string;
  tone?: 'default' | 'warn' | 'success' | 'danger';
}

interface ResultItem {
  id: string;
  type: string;
  title: string;
  subtitle?: string;
  badges?: Badge[];
  link?: string | null;
}

interface PanelDetail {
  id: string;
  conversation_id: string;
  message_id: string | null;
  tool_name: string;
  tool_args: Record<string, unknown>;
  items: ResultItem[];
  total: number;
  truncated: boolean;
  created_at: string;
  answer_text: string | null;
}

interface PanelSummary {
  id: string;
  tool_name: string;
  total: number;
  truncated: boolean;
  created_at: string;
  user_question: string | null;
}

interface Props {
  conversationId: string | null;
  activePanelId: string | null;
  onActivePanelChange: (id: string | null) => void;
  isMobile: boolean;
  /** Drawer schließt sich auf Mobile beim Link-Klick */
  onClose: () => void;
}

function buildHeaders(userId: string, email: string, role: string): HeadersInit {
  return {
    'X-Portal-User-Id': userId,
    'X-Portal-User-Email': email,
    'X-Portal-User-Role': role,
  };
}

export default function AssistantResultPanel({
  conversationId,
  activePanelId,
  onActivePanelChange,
  isMobile,
  onClose,
}: Props) {
  const { user } = useAuth();
  const effectiveUserId = user?.id ?? '';
  const effectiveEmail = user?.email ?? '';
  const effectiveRole = user?.role ?? 'default';
  const navigate = useNavigate();

  const [panels, setPanels] = useState<PanelSummary[]>([]);
  const [detail, setDetail] = useState<PanelDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const headers = useMemo(
    () => buildHeaders(effectiveUserId, effectiveEmail, effectiveRole),
    [effectiveUserId, effectiveEmail, effectiveRole],
  );

  // Panels-Liste der Konversation laden
  useEffect(() => {
    if (!conversationId) {
      setPanels([]);
      setDetail(null);
      return;
    }
    let cancelled = false;
    authFetch(`${ASSISTANT_BASE}/assistant/conversations/${conversationId}/panels`, { headers })
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        const list = (data.panels ?? []) as PanelSummary[];
        setPanels(list);
        // Wenn kein activePanelId vom Caller, neuestes wählen
        if (!activePanelId && list.length > 0) {
          onActivePanelChange(list[0]!.id);
        }
      })
      .catch((e) => !cancelled && setError(`Panels-Liste: ${(e as Error).message}`));
    return () => {
      cancelled = true;
    };
  }, [conversationId, activePanelId, headers, onActivePanelChange]);

  // Detail des aktiven Panels laden
  useEffect(() => {
    if (!activePanelId) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    authFetch(`${ASSISTANT_BASE}/assistant/panels/${activePanelId}`, { headers })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => {
        if (cancelled) return;
        setDetail(data as PanelDetail);
      })
      .catch((e) => !cancelled && setError((e as Error).message))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [activePanelId, headers]);

  const handleLinkClick = (link: string): void => {
    if (isMobile) onClose();
    navigate(link);
  };

  if (!conversationId) {
    return (
      <div className="p-6 text-sm text-muted-foreground text-center bg-card h-full">
        Noch keine Konversation gestartet — frag den Assistenten zuerst etwas.
      </div>
    );
  }

  if (panels.length === 0 && !loading) {
    return (
      <div className="p-6 text-sm text-muted-foreground text-center bg-card h-full">
        Keine Ergebnisse in dieser Konversation. Stelle eine Listen-Frage
        („welche Aufgaben sind offen?"), und die Treffer erscheinen hier.
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-card text-foreground">
      {/* Header — Dropdown */}
      <div className="border-b border-default px-3 py-2 bg-muted shrink-0">
        <select
          value={activePanelId ?? ''}
          onChange={(e) => onActivePanelChange(e.target.value || null)}
          className="w-full bg-card border border-default rounded text-sm px-2 py-2 md:py-1.5 text-foreground"
          aria-label="Letzte Ergebnisse"
        >
          {panels.map((p) => (
            <option key={p.id} value={p.id}>
              {(p.user_question?.trim() || p.tool_name)} — {p.total} {p.total === 1 ? 'Treffer' : 'Treffer'}
              {p.truncated ? ' (gekürzt)' : ''} · {fmtTime(p.created_at)}
            </option>
          ))}
        </select>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        {loading && (
          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground py-8">
            <Loader2 size={16} className="animate-spin" />
            <span>Panel wird geladen …</span>
          </div>
        )}
        {error && (
          <div className="m-3 text-xs italic text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 rounded">
            Fehler: {error}
          </div>
        )}
        {detail && !loading && (
          <>
            {/* Antwort-Text oben */}
            {detail.answer_text && (
              <div className="px-3 py-3 border-b border-default text-sm text-foreground bg-muted/40 whitespace-pre-wrap">
                {detail.answer_text}
              </div>
            )}

            {/* Items-Render: Mobile Cards, Desktop Tabelle */}
            {isMobile ? (
              <ul className="divide-y divide-[color:var(--color-border-default)]">
                {detail.items.map((item) => (
                  <CardRow key={item.id} item={item} onClick={handleLinkClick} />
                ))}
              </ul>
            ) : (
              <table className="w-full text-sm">
                <tbody>
                  {detail.items.map((item) => (
                    <TableRow key={item.id} item={item} onClick={handleLinkClick} />
                  ))}
                </tbody>
              </table>
            )}

            {detail.truncated && (
              <div className="px-3 py-2 text-xs italic text-muted-foreground border-t border-default">
                Liste gekürzt — bitte präziser fragen für die restlichen Treffer.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function CardRow({ item, onClick }: { item: ResultItem; onClick: (link: string) => void }) {
  const clickable = Boolean(item.link);
  return (
    <li
      onClick={() => clickable && item.link && onClick(item.link)}
      className={`px-3 py-3 ${clickable ? 'cursor-pointer hover:bg-muted active:bg-muted' : ''}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-medium text-sm text-foreground truncate">{item.title}</div>
          {item.subtitle && (
            <div className="text-xs text-muted-foreground truncate mt-0.5">{item.subtitle}</div>
          )}
          {item.badges && item.badges.length > 0 && (
            <div className="flex gap-1 mt-1.5 flex-wrap">
              {item.badges.map((b, i) => (
                <BadgeView key={i} badge={b} />
              ))}
            </div>
          )}
        </div>
        {clickable && <ExternalLink size={14} className="shrink-0 text-muted-foreground mt-1" />}
      </div>
    </li>
  );
}

function TableRow({ item, onClick }: { item: ResultItem; onClick: (link: string) => void }) {
  const clickable = Boolean(item.link);
  return (
    <tr
      onClick={() => clickable && item.link && onClick(item.link)}
      className={`border-b border-default ${clickable ? 'cursor-pointer hover:bg-muted' : ''}`}
    >
      <td className="px-3 py-2 align-top">
        <div className="font-medium text-foreground">{item.title}</div>
        {item.subtitle && <div className="text-xs text-muted-foreground mt-0.5">{item.subtitle}</div>}
      </td>
      <td className="px-3 py-2 align-top">
        {item.badges && item.badges.length > 0 && (
          <div className="flex gap-1 flex-wrap">
            {item.badges.map((b, i) => (
              <BadgeView key={i} badge={b} />
            ))}
          </div>
        )}
      </td>
      <td className="px-2 py-2 align-top w-6 text-right">
        {clickable && <ExternalLink size={14} className="text-muted-foreground inline" />}
      </td>
    </tr>
  );
}

function BadgeView({ badge }: { badge: Badge }) {
  const tone = badge.tone ?? 'default';
  const colorClasses: Record<string, string> = {
    default: 'bg-muted text-muted-foreground',
    warn: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
    success: 'bg-brand-100 text-brand-800 dark:bg-brand-900/30 dark:text-brand-300',
    danger: 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300',
  };
  return (
    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${colorClasses[tone]}`}>
      {badge.label}
    </span>
  );
}

function fmtTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}
